<template>
    <div class="tg-page-root min-h-screen bg-gray-50 dark:bg-gray-950">
        <!-- Header -->
        <PageHeader :title="$t('PageTitle_Account')" @back="goBack" />

        <div class="px-4 py-4 pb-24 space-y-4">
            <!-- User Info Card -->
            <div class="bg-white dark:bg-gray-900 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-gray-800 flex items-center gap-3">
                <div class="w-11 h-11 bg-brand/10 rounded-full flex items-center justify-center text-brand text-lg font-bold">
                    {{ userInitial }}
                </div>
                <div class="flex-1 overflow-hidden">
                    <div class="font-semibold text-gray-900 dark:text-gray-100 truncate">{{ userInfo.UserName || userInfo.Email }}</div>
                    <div class="text-xs text-gray-500 dark:text-gray-300 truncate">{{ userInfo.Email }}</div>
                </div>
            </div>

            <div class="text-xs text-gray-400 dark:text-gray-300 uppercase tracking-[0.18em] px-1">{{ $t('Profile') || 'Profile' }}</div>
            <div class="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden divide-y divide-gray-100 dark:divide-gray-800">
                <router-link
                    :to="{ name: 'OrdersView' }"
                    class="account-row group text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                    <div class="account-row-left">
                        <div class="account-row-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                            </svg>
                        </div>
                        <span class="font-medium">{{ $t('My_Orders') }}</span>
                    </div>
                    <el-icon class="text-gray-300 dark:text-gray-400"><ArrowRightBold /></el-icon>
                </router-link>
                <router-link
                    :to="{ name: 'InvitationView' }"
                    class="account-row group text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                    <div class="account-row-left">
                        <div class="account-row-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                        </div>
                        <span class="font-medium">{{ $t('My_Invitations') }}</span>
                    </div>
                    <el-icon class="text-gray-300 dark:text-gray-400"><ArrowRightBold /></el-icon>
                </router-link>
                <router-link
                    :to="{ name: 'coupons' }"
                    class="account-row group text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                    <div class="account-row-left">
                        <div class="account-row-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                            </svg>
                        </div>
                        <span class="font-medium">{{ $t('My_Coupons') }}</span>
                    </div>
                    <el-icon class="text-gray-300 dark:text-gray-400"><ArrowRightBold /></el-icon>
                </router-link>
            </div>

            <div class="text-xs text-gray-400 dark:text-gray-300 uppercase tracking-[0.18em] px-1 pt-2">{{ $t('Settings') }}</div>
            <div class="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden divide-y divide-gray-100 dark:divide-gray-800">
                <button
                    type="button"
                    class="w-full px-4 py-4 flex items-center justify-between gap-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 active:bg-gray-100 dark:active:bg-gray-800 transition-colors"
                    @click="openLanguageSheet"
                >
                    <div class="flex items-center gap-3 min-w-0">
                        <div class="account-row-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                            </svg>
                        </div>
                        <span class="text-[15px] font-medium text-gray-900 dark:text-gray-100">{{ $t('Language') }}</span>
                    </div>
                    <div class="flex items-center gap-2 min-w-0 text-gray-500 dark:text-gray-300">
                        <span class="truncate text-sm font-semibold">{{ currentLanguageName }}</span>
                        <el-icon class="text-gray-300 dark:text-gray-400 flex-shrink-0"><ArrowRightBold /></el-icon>
                    </div>
                </button>
                <div class="px-4 py-4">
                    <div class="flex items-center gap-3 mb-3">
                        <div class="account-row-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                            </svg>
                        </div>
                        <span class="text-[15px] font-medium text-gray-900 dark:text-gray-100">{{ $t('Theme_Appearance') }}</span>
                    </div>
                    <div
                        class="grid grid-cols-3 gap-2 p-1 rounded-2xl bg-gray-100 dark:bg-gray-950 border border-gray-200 dark:border-gray-800"
                        role="group"
                        :aria-label="$t('Theme_Appearance')"
                    >
                        <button
                            v-for="option in themeOptions"
                            :key="option.mode"
                            type="button"
                            class="min-h-9 rounded-xl text-[13px] font-semibold transition-colors"
                            :class="themeMode === option.mode
                                ? 'bg-white dark:bg-gray-700 text-brand dark:text-violet-300 shadow-sm'
                                : 'bg-transparent text-gray-500 dark:text-gray-400'"
                            @click="selectThemeMode(option.mode)"
                        >
                            {{ $t(option.labelKey) }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="text-xs text-gray-400 dark:text-gray-300 uppercase tracking-[0.18em] px-1 pt-2">{{ $t('Other') }}</div>
            <div class="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm overflow-hidden divide-y divide-gray-100 dark:divide-gray-800">
                <router-link
                    :to="{ name: 'LegalPolicyView', params: { policyType: 'terms' } }"
                    class="account-row group text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                    <div class="account-row-left">
                        <div class="account-row-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                        </div>
                        <span class="font-medium">{{ $t('Terms_and_conditions') }}</span>
                    </div>
                    <el-icon class="text-gray-300 dark:text-gray-400"><ArrowRightBold /></el-icon>
                </router-link>
                <router-link
                    :to="{ name: 'LegalPolicyView', params: { policyType: 'privacy' } }"
                    class="account-row group text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                    <div class="account-row-left">
                        <div class="account-row-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                        </div>
                        <span class="font-medium">{{ $t('Privacy_notes') }}</span>
                    </div>
                    <el-icon class="text-gray-300 dark:text-gray-400"><ArrowRightBold /></el-icon>
                </router-link>
                <router-link
                    :to="{ name: 'ReturnPolicyView' }"
                    class="account-row group text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                    <div class="account-row-left">
                        <div class="account-row-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                            </svg>
                        </div>
                        <span class="font-medium">{{ $t('ReturnPolicy_Title') }}</span>
                    </div>
                    <el-icon class="text-gray-300 dark:text-gray-400"><ArrowRightBold /></el-icon>
                </router-link>
                <router-link
                    :to="{ name: 'ContactView' }"
                    class="account-row group text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                    <div class="account-row-left">
                        <div class="account-row-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                        </div>
                        <span class="font-medium">{{ $t('Contact_us') }}</span>
                    </div>
                    <el-icon class="text-gray-300 dark:text-gray-400"><ArrowRightBold /></el-icon>
                </router-link>
            </div>

            <div v-if="showLogout" class="pt-2">
                <button
                    @click="handleLogout"
                    class="w-full bg-white dark:bg-gray-900 text-red-500 dark:text-red-400 font-medium py-3.5 rounded-2xl border border-red-100 dark:border-red-900/40 shadow-sm active:scale-[0.99] transition-all flex items-center justify-center gap-2"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    {{ $t('Logout') }}
                </button>
            </div>
        </div>
        <transition name="fade">
            <div
                v-if="showLanguageSheet"
                class="language-sheet-overlay fixed inset-0 z-[85] bg-black/45"
                @click.self="closeLanguageSheet"
            ></div>
        </transition>
        <transition name="slide-up">
            <div
                v-if="showLanguageSheet"
                class="language-sheet-wrap mobile-frame-fixed tg-bottom-sheet-anchor fixed bottom-0 z-[90]"
            >
                <div class="tg-bottom-sheet-panel mx-auto w-full max-w-[430px] overflow-hidden rounded-t-3xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-2xl">
                    <div class="flex justify-center pt-2.5 pb-2">
                        <div class="h-1 w-10 rounded-full bg-gray-300 dark:bg-gray-600"></div>
                    </div>
                    <div class="flex items-center justify-between px-5 pb-3">
                        <h3 class="text-lg font-bold text-gray-900 dark:text-gray-100">{{ $t('Language') }}</h3>
                        <button
                            type="button"
                            class="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-colors active:scale-95 dark:bg-gray-800 dark:text-gray-300"
                            @click="closeLanguageSheet"
                        >
                            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    <div
                        ref="languageSheetListRef"
                        class="max-h-[58vh] overflow-y-auto overflow-x-hidden tg-sheet-scroll px-3 pb-3"
                    >
                        <button
                            v-for="lang in availableLanguages"
                            :key="lang.code"
                            type="button"
                            :data-selected="locale === lang.code ? 'true' : 'false'"
                            class="flex min-h-[52px] w-full items-center gap-3 rounded-2xl px-3 text-left transition-colors"
                            :class="locale === lang.code
                                ? 'bg-brand/10 text-brand'
                                : 'text-gray-800 hover:bg-gray-50 active:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-800 dark:active:bg-gray-800'"
                            @click="selectLanguage(lang.code)"
                        >
                            <span class="text-xl">{{ lang.flag }}</span>
                            <span class="flex-1 font-semibold">{{ lang.name }}</span>
                            <svg v-if="locale === lang.code" class="h-5 w-5 flex-shrink-0 text-brand" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup>
import { computed, ref, onMounted, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router'
import { showAppNotification } from '@/utils/appNotification'
import { showAppConfirm } from '@/utils/telegramDialog'
import { removeStorage } from '@/utils/storage'
import { STORAGE_KEYS } from '@/constants/storageKeys'
import PageHeader from '@/components/PageHeader.vue'
import { authApi } from '@/api/auth'
import logger from '@/utils/logger'
import { themeMode, THEME_MODES, setThemeMode } from '@/utils/theme'
import {
    availableLanguages,
    getCurrentLanguageName,
    setManualLanguagePreference
} from '@/utils/language'
import { isLogoutAllowed } from '@/utils/telegramEnv'

const router = useRouter()
const { t, locale } = useI18n();
const userInfo = ref({})
const userInitial = ref('')
const showLanguageSheet = ref(false)
const showLogout = computed(() => isLogoutAllowed())
const languageSheetListRef = ref(null)

const currentLanguageName = computed(() => getCurrentLanguageName(locale.value))

const scrollLanguageSheetToSelection = () => {
    nextTick(() => {
        const list = languageSheetListRef.value
        if (!list) return
        list.querySelector('[data-selected="true"]')?.scrollIntoView({ block: 'center', inline: 'nearest' })
    })
}

const openLanguageSheet = () => {
    showLanguageSheet.value = true
    scrollLanguageSheetToSelection()
}

const closeLanguageSheet = () => {
    showLanguageSheet.value = false
}

watch(showLanguageSheet, (visible) => {
    if (visible) {
        scrollLanguageSheetToSelection()
    }
})

const themeOptions = [
    { mode: THEME_MODES.SYSTEM, labelKey: 'Theme_Mode_System' },
    { mode: THEME_MODES.LIGHT, labelKey: 'Theme_Mode_Light' },
    { mode: THEME_MODES.DARK, labelKey: 'Theme_Mode_Dark' },
]

const selectThemeMode = (mode) => {
    setThemeMode(mode)
}

const selectLanguage = (langCode) => {
    locale.value = langCode
    setManualLanguagePreference(langCode)
    closeLanguageSheet()
}


onMounted(async () => {
    try {
        const res = await authApi.getUserInfo();
        if (res.data && res.data.data) {
            userInfo.value = res.data.data;
            // Set user initial
            const name = userInfo.value.UserName || userInfo.value.Email || '';
            userInitial.value = name.charAt(0).toUpperCase();
        }
    } catch (error) {
        logger.error('Failed to fetch user info', error);
    }
})

const goBack = () => {
    router.push('/')
}

const handleLogout = async () => {
    if (!isLogoutAllowed()) {
        return
    }

    const confirmed = await showAppConfirm(
        t('Logout_Confirm_Message'),
        t('Logout_Confirm_Title'),
        {
            confirmButtonText: t('Confirm'),
            cancelButtonText: t('Cancel'),
            type: 'warning',
        },
    )

    if (!confirmed) {
        return
    }

    removeStorage(STORAGE_KEYS.TOKEN)

    showAppNotification({
        type: 'success',
        message: t('Logout_Success'),
        duration: 2000,
        position: 'bottom-right'
    })

    router.push('/login')
}
</script>


<style scoped>
.account-row {
    min-height: 58px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 16px;
    font-size: 15px;
    transition: background-color 0.2s ease, color 0.2s ease;
}

.account-row-left {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}

.account-row-icon {
    width: 32px;
    height: 32px;
    border-radius: 9999px;
    background: rgba(139, 92, 246, 0.12);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.el-avatar {
    background: white !important;
}

:deep(.el-dialog__title) {
    color: #8B5CF6 !important;
}

:deep(.scrollbar-hide::-webkit-scrollbar) {
    display: none !important;
}

:deep(.scrollbar-hide) {
    -ms-overflow-style: none !important;
    scrollbar-width: none !important;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
    transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-up-enter-from,
.slide-up-leave-to {
    transform: translateY(100%);
    opacity: 0;
}

:global(.mobile-frame-fixed.slide-up-enter-from),
:global(.mobile-frame-fixed.slide-up-leave-to) {
    transform: translateX(-50%) translateY(100%);
}

:global(body.tg-webapp .mobile-frame-fixed.slide-up-enter-from),
:global(body.tg-webapp .mobile-frame-fixed.slide-up-leave-to),
:global(body.tg-fullscreen .mobile-frame-fixed.slide-up-enter-from),
:global(body.tg-fullscreen .mobile-frame-fixed.slide-up-leave-to) {
    transform: translateY(100%);
}
</style>
